import React, { useState, useEffect } from 'react';
import { AlertCircle, AlertTriangle, WifiOff } from 'lucide-react';
import ModalPortal from '../../../components/common/ModalPortal';
import { Button } from '../../../components/common/Button';
import { useNavigate } from 'react-router-dom';
import { useLiveAPIContext } from '../contexts/LiveAPIContext';

interface ConnectionErrorModalProps {
  visible: boolean;
  errorType: 'quota_exceeded' | 'connection_error' | 'general_error';
  onDismiss: () => void;
}

/**
 * ConnectionErrorModal - Modal for displaying connection errors
 * 
 * This component is responsible for:
 * 1. Displaying appropriate error messages based on error type
 * 2. Providing retry and navigation options
 * 3. Showing different UI based on error severity
 * 4. Handling reconnection attempts when "Try Again" is clicked
 */
const ConnectionErrorModal: React.FC<ConnectionErrorModalProps> = ({
  visible,
  errorType,
  onDismiss,
}) => {
  const navigate = useNavigate();
  const liveAPI = useLiveAPIContext();
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [localVisible, setLocalVisible] = useState(false);
  
  // Maintain local visibility state to handle quota exceeded errors properly
  useEffect(() => {
    // Only update local visibility based on the visible prop
    setLocalVisible(visible);
    
    // For quota errors, we keep the modal visible regardless of the incoming props
    if (visible && errorType === 'quota_exceeded') {
      console.log('[ConnectionErrorModal] Quota exceeded error - modal will stay visible');
    }
  }, [visible, errorType]);
  
  // If we're using local visibility control and it's false, return null
  if (!localVisible) return null;

  const getErrorInfo = () => {
    switch (errorType) {
      case 'quota_exceeded':
        return {
          title: 'API Quota Exceeded',
          message: 'We\'ve reached our limit for AI interview sessions. This is typically a temporary issue.',
          description: 'Our API has usage limits to ensure fair access for all users. Please return to the dashboard and try again.',
          icon: <AlertTriangle className="w-6 h-6 text-amber-500" />,
          showRetry: false, // No retry for quota errors
          severity: 'warning'
        };
      case 'connection_error':
        return {
          title: 'Connection Lost',
          message: 'The connection to the AI interviewer has been lost. This could be due to network or server problems.',
          description: 'Check your internet connection and try again. If the problem persists, it might be a temporary server issue.',
          icon: <WifiOff className="w-6 h-6 text-red-500" />,
          showRetry: true,
          severity: 'error'
        };
      case 'general_error':
      default:
        return {
          title: 'Interview Error',
          message: 'There was a problem with the AI interviewer. This might be due to network issues, server problems, or an incompatible model version.',
          description: 'You can try again or return to the dashboard. If the problem persists, please try again later.',
          icon: <AlertCircle className="w-6 h-6 text-red-500" />,
          showRetry: true,
          severity: 'error'
        };
    }
  };

  const { title, message, description, icon, showRetry, severity } = getErrorInfo();
  
  const handleReturnToDashboard = () => {
    // Navigate to dashboard
    navigate('/dashboard');
    onDismiss();
  };

  const handleTryAgain = async () => {
    // No retry attempts for quota exceeded errors
    if (errorType === 'quota_exceeded') {
      console.log('[ConnectionErrorModal] Cannot retry: quota exceeded');
      return;
    }
    
    if (errorType === 'connection_error' && liveAPI) {
      setIsReconnecting(true);
      try {
        // First make sure we're disconnected
        await liveAPI.disconnect();
        
        // Small delay to ensure cleanup is complete
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Then attempt to reconnect
        await liveAPI.connect();
        console.log('[ConnectionErrorModal] Reconnection successful');
        setLocalVisible(false); // Hide the modal locally
        onDismiss(); // Also call the parent's dismiss callback
      } catch (error) {
        console.error('[ConnectionErrorModal] Reconnection failed:', error);
        
        // Check if this is a quota error that occurred during reconnection
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes('quota') || errorMessage.includes('exceeded')) {
          console.log('[ConnectionErrorModal] Quota exceeded during reconnection attempt');
          
          // This needs to be handled by the parent component
          // Keep the modal visible but update it to show quota error
          // The parent component should call setConnectionErrorType('quota_exceeded')
        } else {
          // Show a more user-friendly error message for other errors
          if (error instanceof Error) {
            alert(`Reconnection failed: ${error.message}. Please try again later.`);
          } else {
            alert("Reconnection failed. Please try again later.");
          }
        }
      } finally {
        setIsReconnecting(false);
      }
    } else {
      // For other error types, just dismiss the modal
      setLocalVisible(false);
      onDismiss();
    }
  };

  // Get border color based on severity
  const getBorderColor = () => {
    switch (severity) {
      case 'warning':
        return 'border-amber-500/20';
      case 'error':
      default:
        return 'border-red-500/20';
    }
  };

  // Get background color for icon based on severity
  const getIconBgColor = () => {
    switch (severity) {
      case 'warning':
        return 'bg-amber-500/10';
      case 'error':
      default:
        return 'bg-red-500/10';
    }
  };

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in z-50">
        <div className={`bg-surface-light dark:bg-surface-dark rounded-xl shadow-xl max-w-md w-full border ${getBorderColor()} animate-fade-in-up`}>
          <div className={`p-5 border-b ${getBorderColor()}`}>
            <div className="flex items-center">
              <div className={`p-2 rounded-full ${getIconBgColor()} mr-3`}>
                {icon}
              </div>
              <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
                {title}
              </h3>
            </div>
          </div>
          <div className="p-5">
            <p className="text-base text-text-primary-light dark:text-text-primary-dark mb-2">
              {message}
            </p>
            
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-4">
              {description}
            </p>

            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={handleReturnToDashboard}
                className="w-full sm:w-auto"
                disabled={isReconnecting}
              >
                Return to Dashboard
              </Button>
              
              {showRetry && (
                <Button 
                  variant="primary" 
                  onClick={handleTryAgain}
                  className="w-full sm:w-auto"
                  disabled={isReconnecting || errorType === 'quota_exceeded'}
                >
                  {isReconnecting ? 'Reconnecting...' : 'Try Again'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default ConnectionErrorModal; 